import React, { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

const DISMISS_KEY = "cm-pwa-dismissed-at";
const DISMISS_DURATION_DAYS = 7;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!ts) return false;
    const daysSince = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DURATION_DAYS;
  } catch {
    return false;
  }
}

function loginCount() {
  try {
    return Number(localStorage.getItem("cm_login_count") || 0);
  } catch {
    return 0;
  }
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 768px)").matches;
}

export function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    // Heurística: só mostra automaticamente quando o usuário já fez login
    // no mínimo 3 vezes (sinal de uso recorrente) e está em viewport mobile.
    // Em desktop continuamos respondendo ao beforeinstallprompt do Chrome.
    const recurrentMobileUser = isMobileViewport() && loginCount() >= 3;

    // Android/Chrome: dispara beforeinstallprompt
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (recurrentMobileUser || !isMobileViewport()) setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    // iOS: não tem evento, mostra prompt customizado após 5s
    // APENAS se já bateu o limiar de 3 logins.
    let iosTimer;
    if (isIOS() && recurrentMobileUser) {
      iosTimer = setTimeout(() => setShow(true), 5000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  // Botão "Instalar app" no menu lateral dispara este evento global,
  // permitindo o usuário reabrir o prompt mesmo após ter dispensado antes.
  useEffect(() => {
    const onManualOpen = () => {
      if (isStandalone()) return;
      // Limpa "dismissed" se o usuário pediu explicitamente para abrir.
      try {
        localStorage.removeItem(DISMISS_KEY);
      } catch {
        // ignore
      }
      if (isIOS()) {
        // iOS Safari: abre direto o passo-a-passo (não tem prompt nativo)
        setShow(true);
        setShowIOSGuide(true);
      } else {
        setShow(true);
        // No Chrome/Edge desktop e Android, deferred.prompt fica disponível
        // quando o navegador detectou que é instalável. Se já houver, dispara.
        if (deferred) {
          // delay para o card aparecer antes do prompt nativo
          setTimeout(() => install(), 200);
        }
      }
    };
    window.addEventListener("cm-open-install-prompt", onManualOpen);
    return () => window.removeEventListener("cm-open-install-prompt", onManualOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferred]);

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore storage errors
    }
    setShow(false);
    setShowIOSGuide(false);
  }

  async function install() {
    if (isIOS()) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
    setDeferred(null);
  }

  if (!show) return null;

  return (
    <>
      <div
        className="fixed bottom-20 lg:bottom-4 left-3 right-3 lg:left-auto lg:right-4 lg:w-[360px] z-[60] cm-card p-4 shadow-2xl border-blue-200"
        data-testid="pwa-install-prompt"
        role="dialog"
        aria-label="Instalar app"
      >
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
          aria-label="Fechar"
          data-testid="pwa-dismiss-btn"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
            <Download size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-slate-900 text-sm">
              {loginCount() >= 3 ? "Instale na tela inicial" : "Instalar Clean Manager"}
            </div>
            <div className="text-xs text-slate-600 mt-0.5">
              {loginCount() >= 3
                ? "Você usa direto — instale para abrir com 1 toque, sem digitar URL toda vez."
                : isIOS()
                  ? "Acesse o app direto da tela inicial do iPhone, sem precisar de navegador."
                  : "Instale na tela inicial para acesso rápido e funcionar como app nativo."}
            </div>
            <button
              onClick={install}
              data-testid="pwa-install-btn"
              className="mt-3 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg"
            >
              {isIOS() ? "Ver como instalar" : "Instalar"}
            </button>
          </div>
        </div>
      </div>

      {/* Modal iOS — instruções passo a passo */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-end sm:items-center justify-center p-3"
          onClick={() => setShowIOSGuide(false)}
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-ios-guide"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-lg">Como instalar no iPhone</h3>
              <button
                onClick={() => setShowIOSGuide(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100"
                aria-label="Fechar"
              >
                <X size={18} />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                  1
                </span>
                <div>
                  Toque no botão <strong>Compartilhar</strong>{" "}
                  <Share2 size={14} className="inline align-middle" /> na barra inferior do Safari.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                  2
                </span>
                <div>
                  Role para baixo e toque em <strong>&quot;Adicionar à Tela de Início&quot;</strong>.
                </div>
              </li>
              <li className="flex gap-3">
                <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                  3
                </span>
                <div>
                  Toque em <strong>Adicionar</strong> no canto superior direito.
                </div>
              </li>
            </ol>
            <button
              onClick={dismiss}
              className="mt-5 w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-lg"
            >
              Entendi
            </button>
          </div>
        </div>
      )}
    </>
  );
}
